import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import { cn } from "../../lib/utils";
import {
  smallTextSize,
  primaryText,
  mediumText,
  tertiaryText,
  regularText
} from "../../styling/stylingHelper";
import { currencyFormatter, hasAnyInvoiceLogo } from "../../services/helperService";
import { Platform } from "../../types/Platforms";
import { CardWithHeader } from "./CardWithHeader";
import { SupplierLogo } from "./SupplierLogo";
import { COLORS } from "../orderhistory/utils";
import { useTranslation } from "react-i18next";

interface SideBarChartData {
  supplier: string;
  value: number;
  unknownSupplierName?: string;
}

/**
 * Helper to determine if a supplier string is a valid Platform enum value
 */
function isPlatformValue(supplier: string): boolean {
  return Object.values(Platform).includes(supplier.toLowerCase() as Platform);
}

interface SideBarChartProps {
  data?: SideBarChartData[];
  title?: string;
  seeAllText?: string;
  onSeeAllClick?: () => void;
  /** Optional element rendered on the right of the header (e.g. a maximize button). */
  headerActionsRight?: React.ReactNode;
  /** Max number of suppliers to show. Defaults to 8 (the compact card); pass a large
   *  value to render the full set inside a maximize modal. */
  maxItems?: number;
  /**
   * Whether a label that matches a known supplier resolves to that supplier's LOGO.
   *
   * Defaults to true (every existing caller is supplier-labelled and is unaffected). Pass false
   * when the labels are NOT suppliers — restaurants or products — because the match is by exact
   * name: a restaurant or product called "Illy", "Lidl" or "Action" would otherwise render that
   * supplier's logo in place of its own name, misrepresenting the row rather than just looking
   * odd. With logos off, every label renders as plain text.
   */
  showLogos?: boolean;
}

export const SideBarChart: React.FC<SideBarChartProps> = ({
  data = [],
  title,
  seeAllText,
  onSeeAllClick,
  headerActionsRight,
  maxItems = 8,
  showLogos = true,
}) => {
  const { t } = useTranslation();

  // Use translated defaults if not provided. An explicit empty string suppresses the
  // header entirely (used when this chart is rendered inside an ExpandModal that already
  // shows the title).
  const displayTitle = title === undefined ? t('dashboard.supplierSpending') : title;
  const displaySeeAllText = seeAllText || t('common.seeAll');

  // Only keep the highest spending suppliers (top `maxItems`)
  const topData = useMemo(() => {
    // Always create a sorted copy so as not to reorder the input array
    return [...data].sort((a, b) => b.value - a.value).slice(0, maxItems);
  }, [data, maxItems]);

  const sortedData = useMemo(
    () => [...topData], // Already sorted in topData above
    [topData]
  );
  const maxAmount =
    sortedData.length > 0 ? Math.max(...sortedData.map((item) => item.value)) : 0;

  const [scale, setScale] = useState(0.8);
  const measurementRef = useRef<HTMLDivElement>(null);
  const barAreaRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (measurementRef.current && barAreaRef.current && sortedData.length > 0) {
      const labelElements = measurementRef.current.children;
      let maxWidth = 0;
      for (let i = 0; i < labelElements.length; i++) {
        const el = labelElements[i] as HTMLElement;
        maxWidth = Math.max(maxWidth, el.clientWidth);
      }
      const availableWidth = barAreaRef.current.clientWidth;
      const margin = 8;
      const reservedWidth = maxWidth + margin;
      let newScale = 1;
      if (availableWidth > 0 && reservedWidth < availableWidth) {
        newScale = (availableWidth - reservedWidth) / availableWidth;
      } else {
        newScale = 0;
      }
      setScale(newScale);
    }
  }, [sortedData]);

  // Define a fixed width for the logo area so all bars align, no matter logo width
  var LOGO_SPACE_WIDTH = 80;
  var gapBetweenSuppliers = 'gap-5';
  var barHeight = 'h-8';
  var useMediumLogo = false; // Track which logo dimensions to use

  // if there is more than 6 data passed
  if (sortedData.length > 6) {
    LOGO_SPACE_WIDTH = 60;
    gapBetweenSuppliers = 'gap-4';
    barHeight = 'h-6';
    useMediumLogo = true;
  }

  return (
    <CardWithHeader
      title={displayTitle}
      seeAllText={onSeeAllClick ? displaySeeAllText : undefined}
      onSeeAllClick={onSeeAllClick}
      headerActionsRight={headerActionsRight}
      className="h-full"
    >
      {sortedData.length === 0 ? (
        <div className="flex items-center justify-center flex-1 min-h-0 w-full">
          <p className={cn(smallTextSize, regularText, tertiaryText, "text-center", "w-full")}>
            {t('dashboard.noSupplierDataInPeriod')}
          </p>
        </div>
      ) : (
        <div className={cn("flex flex-col w-full items-start relative flex-1 min-h-0 mr-2", gapBetweenSuppliers)}>
          {sortedData.map((item, index) => {
          const percent = maxAmount > 0 ? (item.value / maxAmount) * 100 : 0;
          const barPercent = percent * scale;
          const firstItemRef = index === 0 && sortedData.length > 0 ? barAreaRef : undefined;
          // Use the supplier's own brand colour (matching the supplier-spending bar chart),
          // falling back to neutral grey for unknown suppliers.
          const barColor =
            (item.unknownSupplierName && COLORS[item.unknownSupplierName.toLowerCase()])
              ? COLORS[item.unknownSupplierName.toLowerCase()]
              : (COLORS[item.supplier.toLowerCase()] || '#a3a3a3');
          return (
            <div
              key={index}
              className="flex items-center relative self-stretch w-full flex-[0_0_auto]"
            >
              {/* Fixed-width logo cell, logo aligned left */}
              <div
                style={{
                  minWidth: LOGO_SPACE_WIDTH,
                  maxWidth: LOGO_SPACE_WIDTH,
                  width: LOGO_SPACE_WIDTH,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start", // Left align the logo
                  marginRight: 0,
                }}
              >
                {/* Check if this is a known platform or an any_invoice supplier. With
                    `showLogos` off the label is not a supplier at all, so both lookups are
                    skipped and it always falls through to the text branch. */}
                {showLogos && isPlatformValue(item.supplier) ? (
                  // Known platform - show logo
                  <SupplierLogo
                    platform={item.supplier as Platform}
                    alt={`${item.supplier} logo`}
                    size={useMediumLogo ? 'medium' : 'large'}
                    style={{ maxWidth: "100%", maxHeight: 32, objectFit: "contain" }}
                  />
                ) : showLogos && hasAnyInvoiceLogo(item.supplier) ? (
                  // any_invoice supplier with logo - show logo
                  <SupplierLogo
                    platform={Platform.ANY_INVOICE}
                    unknownSupplierName={item.supplier}
                    alt={`${item.supplier} logo`}
                    size={useMediumLogo ? 'medium' : 'large'}
                    style={{ maxWidth: "100%", maxHeight: 32, objectFit: "contain" }}
                  />
                ) : (
                  // any_invoice supplier without logo - show text
                  <span className={cn(primaryText, mediumText, smallTextSize)}>
                    {item.supplier}
                  </span>
                )}
              </div>
              {/* Bar and value - now left-aligned together */}
              <div className="flex-1 flex items-center" style={{ minWidth: 0 }} ref={firstItemRef}>
                {/* Bar */}
                <div
                  className={cn("relative", barHeight)}
                  style={{
                    width: `${barPercent}%`,
                    minWidth: barPercent > 0 ? 8 : 0,
                    transition: "width 0.3s",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    className={cn("h-full w-full rounded-lg")}
                    style={{ backgroundColor: barColor }}
                  />
                </div>
                {/* Value directly to the right of the bar, left-aligned */}
                <div
                  style={{
                    minWidth: 0,
                    marginLeft: "8px", // small space between bar and value
                    whiteSpace: "nowrap",
                  }}
                  className={cn(
                    smallTextSize,
                    mediumText,
                    primaryText,
                    "tracking-[-0.14px] leading-[21px]"
                  )}
                >
                  {currencyFormatter.format(item.value)}
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}
      {/* Hidden measurement container for label widths */}
      <div
        ref={measurementRef}
        style={{
          position: "absolute",
          top: "-10000px",
          left: 0,
          pointerEvents: "none",
        }}
      >
        {sortedData.map((item) => (
          <div
            key={item.supplier}
            className={cn(
              smallTextSize,
              mediumText,
              primaryText,
              "tracking-[-0.14px] leading-[21px]"
            )}
          >
            {currencyFormatter.format(item.value)}
          </div>
        ))}
      </div>
    </CardWithHeader>
  );
};